/**
 * Wiring shared by the three bulk edit forms: what the selected entities agree
 * on, which fields already carry a pending change, and the two dispatches a bulk
 * form can make. Keeps the forms themselves declarative field lists.
 */

import { useMemo } from "react";
import { BULK_FIELDS, commonValue, dirtyFields } from "../../shared/bulkFields";

/**
 * @param {Array<Object>} entities - Selected working entities.
 * @param {Array<Object>} serverEntities - Their server snapshots.
 * @param {string} kind - "device", "camera" or "view".
 * @param {Function} dispatch - Tree reducer dispatch.
 * @returns {{publicIds: Array<string>, valueOf: Function, isModified: Function,
 *   dirtyCount: number, setField: Function, revertField: Function}} Form wiring.
 */
export default function useBulkForm(entities, serverEntities, kind, dispatch) {
  const publicIds = useMemo(() => entities.map((entity) => entity.public_id), [entities]);

  const dirty = useMemo(
    () => dirtyFields(serverEntities, entities, BULK_FIELDS[kind], kind),
    [serverEntities, entities, kind]
  );

  const setField = (field) => (value) =>
    dispatch({ type: "BULK_UPDATE", entityType: kind, publicIds, patch: { [field]: value } });

  const revertField = (field) => () => dispatch({ type: "BULK_REVERT_FIELD", entityType: kind, publicIds, field });

  return {
    publicIds,
    valueOf: (field) => commonValue(entities, field, kind),
    isModified: (field) => dirty.has(field),
    dirtyCount: dirty.size,
    setField,
    revertField,
  };
}
